import { FC, HTMLAttributes, memo, useCallback, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import useMwdActions from '@/apps/mwd-v2/hooks/useMwdActions';
import useTooltipActions from '@/apps/mwd-v2/state/actions/useTooltipActions';
import { LeftLegendHoverArea, WerklijnLegendLeft } from '@/apps/mwd-v2/types/legends';
import { preventDefault } from '@/apps/mwd-v2/utils/mwdEvents/eventHelpers';
import IngeregeldXs from '@/assets/icons/IngeregeldXs';
import StarFilledXs from '@/assets/icons/StarFilledXs';
import StarXs from '@/assets/icons/StarXs';
import useSidebarStore from '@/components/sidebar/hooks/useSidebarStore';
import { useVeiligheidsconflictenIds } from '@/state/selectors/useConflicten';
import useClientState from '@/state/useClientState';
import { formatInregelingDate } from '@/utils/dateUtils';
import { Group } from '@visx/group';

import LeftLegendIcon from './LeftLegendIcon';

import styles from './LegendLeft.module.css';

type LegendProps = HTMLAttributes<HTMLOrSVGElement> & {
  legendLeft: WerklijnLegendLeft;
  height: number;
  y: number;
  isAllowedToMutate: boolean;
  isAllowedToInregelingen: boolean;
};

const LegendLeftItem: FC<LegendProps> = ({
  legendLeft,
  height,
  y,
  isAllowedToMutate,
  isAllowedToInregelingen,
  ...divProps
}) => {
  const { setShowLeftLegendTooltip, setShowLeftLegendPerronLengteConflictTooltip, hideTooltipWithTimeout } =
    useTooltipActions();

  const { setMaterieelPanelData, setShowMaterieelPanel, showMaterieelPanel } = useClientState(
    useShallow((state) => ({
      setMaterieelPanelData: state.setMaterieelPanelData,
      setShowMaterieelPanel: state.setShowMaterieelPanel,
      showMaterieelPanel: state.showMaterieelPanel,
    })),
  );

  const selectAllWerklijnen = useClientState((state) => state.selectAllWerklijnen);
  const { verwijderGeselecteerdeWerklijnen } = useMwdActions();
  const clearInzetPanel = useClientState((state) => state.clearInzetPanel);
  const showDetailPanel = useClientState((state) => state.showDetailPanel);
  const setShowContextMenu = useClientState((state) => state.setShowContextMenu);
  const setShowDialog = useClientState((state) => state.setShowDialog);
  const closeContextMenu = useClientState((state) => state.closeContextMenu);
  const isSelected = useClientState((state) => state.selected.werklijnen.includes(legendLeft.werklijnId));
  const nieuweWerklijnIds = useClientState((state) => state.nieuweWerklijnIds);

  const [hoverArea, setHoverArea] = useState<LeftLegendHoverArea>(undefined);
  const [highlightWerklijn, setHighlightWerklijn] = useState<string | null>(null);
  const elementHeight = 128;

  const selectWerklijn = useClientState((state) => state.selectWerklijn);
  const markWerklijnAsFavorite = useClientState((state) => state.markWerklijnAsFavorite);
  const activePanel = useSidebarStore((state) => state.activePanel);
  const setActivePanel = useSidebarStore((state) => state.setActivePanel);

  const { werklijnIds: werklijnIdsMetVeiligheidsconflict } = useVeiligheidsconflictenIds();

  const nummer = legendLeft.eenheid.nummer.toLowerCase();
  const shouldReduceFont = nummer.length > 6;
  const shouldTruncate = nummer.length > 7;

  const displayedNummer = shouldTruncate ? `${nummer.slice(0, 7)}..` : nummer;
  const fontSize = shouldReduceFont ? 14 : 18;

  const onClickLegendLeft = useCallback(
    (event: React.MouseEvent<SVGGElement, MouseEvent>) => {
      let eventKey: 'Ctrl' | 'Shift' | undefined;
      setActivePanel('Mwd');
      if (event.ctrlKey) eventKey = 'Ctrl';
      if (event.shiftKey) eventKey = 'Shift';
      void selectWerklijn(legendLeft.werklijnId, eventKey);
      void closeContextMenu();

      if (showMaterieelPanel || showDetailPanel) {
        clearInzetPanel();
        setShowMaterieelPanel();
        setMaterieelPanelData?.(legendLeft?.eenheid);
      }
    },
    [
      legendLeft,
      selectWerklijn,
      closeContextMenu,
      showMaterieelPanel,
      showDetailPanel,
      clearInzetPanel,
      setShowMaterieelPanel,
      setMaterieelPanelData,
      setActivePanel,
    ],
  );

  const onContextMenuLegendLeft = useCallback(
    (event: React.MouseEvent<SVGGElement, MouseEvent>) => {
      let eventKey: 'Ctrl' | 'Shift' | undefined;
      setActivePanel('Mwd');
      if (event.ctrlKey) eventKey = 'Ctrl';
      if (event.shiftKey) eventKey = 'Shift';
      void selectWerklijn(legendLeft.werklijnId, eventKey, true);
      if (event.ctrlKey && event.button === 0) {
        return preventDefault(event);
      }
      const currentPosition = document.getElementById('mwdSvgContainer')?.getBoundingClientRect();
      const mousePointer = {
        x: (currentPosition?.width ?? 0) - (currentPosition?.right ?? 0) + event.clientX - 30 || 0,
        y: (currentPosition?.height ?? 0) - (currentPosition?.bottom ?? 0) + event.clientY - 30 || 0,
      };

      void setShowContextMenu('leftLegend', legendLeft, mousePointer);
      return preventDefault(event);
    },
    [legendLeft, selectWerklijn, setShowContextMenu, setActivePanel],
  );

  const onKeyDownLegendLeft = useCallback(
    (event: React.KeyboardEvent<SVGGElement>) => {
      if (
        (event.ctrlKey && event.code === 'KeyA') ||
        (event.altKey && ['KeyV', 'KeyI'].includes(event.code)) ||
        event.code === 'Delete'
      ) {
        event.preventDefault();
        event.stopPropagation();
        if (event.code === 'KeyA') void selectAllWerklijnen();
        if (isAllowedToMutate && event.code === 'KeyV') void setShowDialog('versterken', 'sneltoets');
        if (isAllowedToInregelingen && event.code === 'KeyI') void setShowDialog('inregelen', 'sneltoets');
        if (event.code === 'Delete') void verwijderGeselecteerdeWerklijnen();
      }
    },
    [isAllowedToMutate, isAllowedToInregelingen, selectAllWerklijnen, setShowDialog, verwijderGeselecteerdeWerklijnen],
  );

  const handleMaterieelClick = useCallback(() => {
    if (setMaterieelPanelData && legendLeft.eenheid.nummer !== 'MANCO') {
      clearInzetPanel();
      setShowMaterieelPanel();
      setMaterieelPanelData(legendLeft?.eenheid);
    }
  }, [legendLeft, clearInzetPanel, setShowMaterieelPanel, setMaterieelPanelData]);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (hoverArea === 'main') {
        void setShowLeftLegendTooltip(
          {
            x: event.clientX,
            y: event.clientY,
          },
          legendLeft,
        );
      } else if (hoverArea === 'plc') {
        void setShowLeftLegendPerronLengteConflictTooltip(
          {
            x: event.clientX,
            y: event.clientY,
          },
          legendLeft,
        );
      }
    },
    [legendLeft, hoverArea],
  );

  const handleMouseLeave = useCallback(() => {
    setHoverArea(undefined);
    void hideTooltipWithTimeout();
  }, [hideTooltipWithTimeout]);

  const handleMouseEnter = useCallback((area: LeftLegendHoverArea) => setHoverArea(area), []);

  return (
    <Group
      tabIndex={0}
      id={`left-legend-unit-${legendLeft.werklijnId}`}
      data-testid={`left-legend-unit-${legendLeft.werklijnId}`}
      onContextMenu={onContextMenuLegendLeft}
      onClick={onClickLegendLeft}
      onDoubleClick={handleMaterieelClick}
      className={styles.legendLeftItemStyle}
      onMouseEnter={() => handleMouseEnter('main')}
      data-isselected={isSelected && activePanel === 'Mwd'}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onKeyDown={onKeyDownLegendLeft}
      style={{
        outline: 'none',
      }}
      {...divProps}
    >
      <rect
        x={0}
        y={y - elementHeight / 2 - 20}
        width={128}
        height={height}
        fill={
          isSelected && activePanel === 'Mwd'
            ? 'var(--tractie-color-background-selected)'
            : highlightWerklijn === legendLeft.werklijnId || nieuweWerklijnIds.includes(legendLeft.werklijnId)
              ? 'var(--tractie-color-background-accent-yellow-subtle)'
              : 'transparent'
        }
        style={{
          transition: 'fill 0.3s',
          outline: 'none',
        }}
      />
      {legendLeft.isLastFavorite && (
        <line
          x1={0}
          y1={y - elementHeight / 2 - 21 + height}
          x2={128}
          y2={y - elementHeight / 2 - 21 + height}
          stroke='var(--tractie-color-border-emphasis)'
        />
      )}
      {hoverArea && (
        <foreignObject style={{ outline: 'none' }} x={0} y={y - elementHeight / 2 + 4} width='100' height='100'>
          <button
            id={`werklijn-is-favorite-${legendLeft.werklijnId}`}
            data-testid={`werklijn-is-favorite-${legendLeft.werklijnId}`}
            className={styles.starIconStyleV2}
            onClick={(event) => {
              event.stopPropagation();
              markWerklijnAsFavorite(legendLeft.werklijnId);
              setHoverArea(undefined);
              !legendLeft.isFavorite && setHighlightWerklijn(legendLeft.werklijnId);
              setTimeout(() => {
                setHighlightWerklijn(null);
              }, 5000);
            }}
            style={{ outline: 'none' }}
          >
            {legendLeft.isFavorite ? (
              <StarFilledXs
                fill='var(--tractie-color-content-emphasis)'
                data-testid='markedAsFavorite'
                width='16px'
                height='16px'
                data-dtname='favorite'
                aria-hidden='true'
              />
            ) : (
              <StarXs
                fill='var(--tractie-color-content-emphasis)'
                data-testid='unmarked'
                width='16px'
                height='16px'
                data-dtname='favorite'
                aria-hidden='true'
              />
            )}
          </button>
        </foreignObject>
      )}
      <Group data-testid={`legendLeft-${legendLeft.werklijnId}`}>
        <text
          x={25}
          y={y - elementHeight / 3 - 1}
          style={{ fontSize }}
          className={styles.legendLeftUnitNumberStyleV2}
          data-testid={`eenheid-nummer-${legendLeft.eenheid.nummer.toLowerCase()}`}
        >
          {displayedNummer}
        </text>
        {legendLeft.eenheid?.inregeling ? (
          <Group top={8}>
            <IngeregeldXs
              x={22}
              y={y - elementHeight / 3 + 3}
              style={{ fill: 'var(--tractie-color-content-emphasis)' }}
              data-testid='inregeling-icon'
              width='16px'
              height='16px'
              aria-hidden='true'
            />
            <text
              x={40}
              y={y - elementHeight / 5}
              fontSize={14}
              style={{ fill: 'var(--tractie-color-content-emphasis' }}
              data-testid='inregeldatum'
            >
              {formatInregelingDate(legendLeft.eenheid.inregeling?.datum)}
            </text>
          </Group>
        ) : (
          <text x={25} y={y - elementHeight / 5 + 4} fontSize='14px' fill='var(--tractie-color-content-placeholder)'>
            {legendLeft.details.materieelSoortEersteInzet}
          </text>
        )}
        <foreignObject x={90} y={y - elementHeight / 2 + 2} width='33' height='28'>
          <LeftLegendIcon
            eenheid={legendLeft.eenheid}
            veiligheidsconflict={werklijnIdsMetVeiligheidsconflict.includes(legendLeft.werklijnId)}
            werklijnId={legendLeft.werklijnId}
            setHoverArea={setHoverArea}
          />
        </foreignObject>
      </Group>
    </Group>
  );
};

export default memo(LegendLeftItem);
